(ns app.front.view.profile.email-notifications
  "Email notification preferences for the profile pages of authority- and stakeholder users"
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.util :as futil]
            [app.front.view.profile.subs :as profile-subs]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.user.model :as user-model]
            [goog.functions]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::notification-on?
  :<- [::profile-subs/form-data]
  (fn [form-data [_ notification-type]]
    (-> form-data
        :user/notification-types
        (contains? notification-type))))

(rf/reg-sub ::payment-notifications?
  :<- [::ff/enabled? :feature-flag.id/payment-emails]
  :<- [:session/authority-user?]
  :<- [:session/reviewer-user?]
  :<- [:session/inspector-user?]
  :<- [:session/cashier-user?]
  (fn [[payment-emails? authority? reviewer? inspector? cashier?]]
    (and payment-emails?
         (or authority? reviewer? inspector? cashier?))))

(rf/reg-event-fx ::toggle-notification-type
  (fn [{:keys [db]} [_ notification-type value]]
    (let [prev-value (or (-> db ::profile-subs/form-data :user/notification-types)
                         #{})
          new-value  (if value
                       (conj prev-value notification-type)
                       (disj prev-value notification-type))]
      {:dispatch [::profile-subs/set-form-data [:user/notification-types] new-value]})))

(defn active-notification-types
  "Returns an array of active notification toggles which is a subset of all different notification types
  defined in core.user.model/notification-types"
  []
  (let [authority-groups                   (<sub [:session/authority-groups])
        is-authority?                      (seq authority-groups)
        has-write-auth?                    (some (comp :group.role/write-auth :group-roles) authority-groups)
        has-feature?                       (fn [feature]
                                             (some #(group-model/has-feature? % feature) authority-groups))
        has-inspections?                   (has-feature? :group.feature/inspections)
        has-circulations?                  (seq (set/intersection (->> authority-groups
                                                                       (mapcat group-model/get-scope-permit-types)
                                                                       set)
                                                                  ptc-model/permit-types-with-circulations))
        is-reviewer?                       (<sub [:session/reviewer-user?])
        send-review-email?                 (<sub [::ff/enabled? :feature-flag.id/assigned-review-email])
        send-review-due-reminders?         (<sub [::ff/enabled? :feature-flag.id/review-due-date])
        condition-due-dates?               (<sub [::ff/enabled? :feature-flag.id/condition-due-dates])
        comply-by-date-notifications?      (<sub [::ff/enabled? :feature-flag.id/comply-by-date-notifications])
        condition-due-date-reminders?      (<sub [::ff/enabled? :feature-flag.id/condition-due-date-reminders])
        send-circulation-request-email?    (<sub [::ff/enabled? :feature-flag.id/assignee-receive-email-on-circulation-request])
        add-wo-flags?                      (<sub [::ff/enabled? :feature-flag.id/pwo-notification-flags])
        send-circulation-bounce-email?     (<sub [::ff/enabled? :feature-flag.id/bounced-circulation-notification])
        is-ce-authority?                   (<sub [:session/code-enforcement-authority?])
        is-wo-authority?                   (<sub [:session/work-orders-authority?])
        has-bonds?                         (has-feature? :group.feature/use-bond-accounts)
        is-bond-admin?                     (<sub [:session/bond-admin?])
        is-property-manager?               (<sub [:session/property-manager?])
        property-unverified-notifications? (<sub [::ff/enabled? :feature-flag.id/property-verification])
        active-notifications               (concat
                                             (when is-authority?
                                               [:user.notification-type/new-workspace-submitted])
                                             (when has-inspections?
                                               [:user.notification-type/inspection-request-submitted
                                                :user.notification-type/inspection-request-modified])
                                             (when is-authority?
                                               [:user.notification-type/direct-message
                                                :user.notification-type/public-message
                                                :user.notification-type/requested-changes-marked-done])
                                             (when has-circulations?
                                               (concat [:user.notification-type/circulation-completed
                                                        :user.notification-type/circulation-due]
                                                       (when send-circulation-bounce-email?
                                                         [:user.notification-type/circulation-unsuccessful])))
                                             (when is-authority?
                                               [:user.notification-type/attachment-added-or-deleted-in-reviewed-workspace
                                                :user.notification-type/attachment-modified-in-assigned-workspace])
                                             (when has-inspections?
                                               [:user.notification-type/attachment-modified-in-inspector-workspace])
                                             (when (and is-reviewer?
                                                        send-review-email?)
                                               [:user.notification-type/application-assigned
                                                :user.notification-type/previous-review-completed])
                                             (when (and send-review-due-reminders?
                                                        (or is-reviewer? is-authority?))
                                               [:user.notification-type/review-due])
                                             (when (and has-inspections?
                                                        is-authority?
                                                        comply-by-date-notifications?)
                                               [:user.notification-type/deficiency-comply-by-date-approaches])
                                             (when (and is-authority?
                                                        condition-due-dates?
                                                        condition-due-date-reminders?
                                                        (has-feature? :group.feature/condition-due-date))
                                               [:user.notification-type/condition-due-date-approaches])
                                             (when (and is-authority?
                                                        has-write-auth?
                                                        send-circulation-request-email?)
                                               [:user.notification-type/circulation-request-completed-for-workspace-assignee])
                                             (when (<sub [::payment-notifications?])
                                               [:user.notification-type/payment-completed])
                                             (when (and add-wo-flags? (or is-ce-authority? is-wo-authority?))
                                               [:user.notification-type/complaint-created])
                                             (when (and add-wo-flags? is-wo-authority?)
                                               [:user.notification-type/work-order-created
                                                :user.notification-type/work-order-assigned-to-you
                                                :user.notification-type/work-order-in-progress
                                                :user.notification-type/work-order-completed
                                                :user.notification-type/work-order-cancelled])
                                             (when (and add-wo-flags? is-wo-authority? has-inspections?)
                                               [:user.notification-type/work-task-created
                                                :user.notification-type/work-task-assigned-to-you
                                                :user.notification-type/work-task-in-progress
                                                :user.notification-type/work-task-completed
                                                :user.notification-type/work-task-cancelled])
                                             (when (and has-bonds? is-bond-admin?)
                                               [:user.notification-type/bond-verification-needed
                                                :user.notification-type/bond-verified
                                                :user.notification-type/bond-about-to-expire
                                                :user.notification-type/bond-expired
                                                :user.notification-type/bond-closed
                                                :user.notification-type/bond-updated
                                                :user.notification-type/bond-reopened])
                                             (when (and is-property-manager?
                                                        property-unverified-notifications?)
                                               [:user.notification-type/property-verification-needed]))]
    (->> active-notifications
         (remove nil?)
         (filter #(some #{%} user-model/notification-types)))))

(defn email-toggles []
  (when (<sub [:session/authority-user?])
    (let [tr (<sub [:tr/quiet])]
      [:fieldset
       [:legend.mb-6.font-semibold.text-base (tr [:user/email-notifications])]
       [:div.pb-4 (tr [:user/email-notifications-sub-header])]
       [:div.flex.flex-wrap
        (futil/for-frag [notification-type (active-notification-types)
                         :let [text              (tr [notification-type])
                               help-text-keyword (keyword (str (namespace notification-type)
                                                               ".help-text/"
                                                               (name notification-type)))
                               help-text-note-kw (keyword (str (namespace notification-type)
                                                               ".help-text.note/"
                                                               (name notification-type)))
                               help-text         (tr [help-text-keyword])
                               help-text-note    (tr [help-text-note-kw])
                               is-on?            (<sub [::notification-on? notification-type])
                               toggle-fn         #(>evt [::toggle-notification-type notification-type %])]]
          [:div.flex.items-start.pb-3.w-full
           [:div.flex-none.w-28
            (cc/toggle {:id         (str "toggle-" (name notification-type))
                        :disabled   false
                        :toggled    is-on?
                        :aria-label "name"
                        :class-name "unlabeled-toggle"
                        :on-toggle  toggle-fn})]
           [:div
            [:p.pb-1.font-bold text]
            [:p.pb-1
             help-text
             (when help-text-note
               [:span.italic (str " " help-text-note)])]]])]])))
